import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  Edit,
  Filter,
  Lock,
  Mail,
  Phone,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  SkipForward,
  Unlock,
  UserCheck,
  UserCog,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;
const ROLES = ['All', 'Admin', 'Driver', 'Employee', 'Client'];
const FORM_ROLES = ['Admin', 'Driver', 'Employee', 'Client'];
const STATUSES = ['All', 'Active', 'Blocked'];

const createEmptyUser = () => ({
  UserName: '',
  Password: '',
  CPassword: '',
  Role: 'Client',
  ClientId: '',
  EmployeeId: '',
  Client: { Name: '', MobileNo1: '', EmailId: '' },
  Employee: {},
  IsSkip: false,
});

const getStatus = (response) => {
  if (typeof response === 'string') return response.trim();
  return String(response?.Status ?? response?.status ?? response?.Message ?? response?.message ?? '').trim();
};

const getMessage = (response, fallback) => {
  if (typeof response === 'string' && response.trim()) return response.trim();
  return response?.Message || response?.message || response?.Status || response?.status || fallback;
};

const initials = (value) => {
  const text = (value || '').trim();
  if (!text) return 'U';
  return text
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const roleStyle = (role) => {
  switch (role) {
    case 'Admin':
      return 'bg-purple-100 text-purple-700 ring-purple-200';
    case 'Driver':
      return 'bg-blue-100 text-blue-700 ring-blue-200';
    case 'Employee':
      return 'bg-amber-100 text-amber-700 ring-amber-200';
    case 'Client':
      return 'bg-emerald-100 text-emerald-700 ring-emerald-200';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({ Total: 0, Active: 0, Blocked: 0, Admin: 0, Driver: 0, Employee: 0, Client: 0 });
  const [employees, setEmployees] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientKeyword, setClientKeyword] = useState('');
  const [mobileKeyword, setMobileKeyword] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [formData, setFormData] = useState(createEmptyUser());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userToBlock, setUserToBlock] = useState(null);
  const clientSearchSeq = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isEditing = Boolean(formData.Id);
  const isClientRole = formData.Role === 'Client';

  const loadEmployees = useCallback(async (employeeId = formData.EmployeeId || 0) => {
    try {
      const response = await MainService.EmployeesInUserDropDownV2({ EmployeeId: employeeId || 0 });
      setEmployees(Array.isArray(response) ? response : response?.Data || []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    }
  }, [formData.EmployeeId]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.UsersInListV2({
        Keyword: activeSearchKeyword,
        Type: roleFilter,
        Status: statusFilter,
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });

      setUsers(Array.isArray(response?.Data) ? response.Data : []);
      setTotalCount(Number(response?.Total) || 0);
      if (response?.Summary) {
        setSummary(response.Summary);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please check whether the V2 user API is deployed.');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeSearchKeyword, currentPage, roleFilter, statusFilter]);

  const loadClients = useCallback(async (keyword) => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) {
      setClientOptions([]);
      return;
    }

    const seq = clientSearchSeq.current + 1;
    clientSearchSeq.current = seq;

    try {
      setClientLoading(true);
      const response = await MainService.ClientsInUserListV2({
        Keyword: trimmed,
        ClientId: formData.ClientId || 0,
        Skip: 0,
        Take: PAGE_SIZE,
      });

      if (clientSearchSeq.current !== seq) return;
      setClientOptions(Array.isArray(response?.Data) ? response.Data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      if (clientSearchSeq.current === seq) setClientOptions([]);
    } finally {
      if (clientSearchSeq.current === seq) setClientLoading(false);
    }
  }, [formData.ClientId]);

  useEffect(() => {
    loadEmployees(0);
  }, [loadEmployees]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientDropdownOpen) loadClients(clientKeyword);
    }, 250);
    return () => clearTimeout(timer);
  }, [clientDropdownOpen, clientKeyword, loadClients]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (mobileDropdownOpen) loadClients(mobileKeyword);
    }, 250);
    return () => clearTimeout(timer);
  }, [mobileDropdownOpen, mobileKeyword, loadClients]);

  const selectedAccountName = isClientRole ? formData.Client?.Name : formData.Employee?.Name;
  const selectedAccountMobile = isClientRole ? formData.Client?.MobileNo1 : formData.Employee?.MobileNo1;
  const selectedAccountEmail = isClientRole ? formData.Client?.EmailId : formData.Employee?.EmailId;

  const resetForm = () => {
    setFormData(createEmptyUser());
    setClientKeyword('');
    setMobileKeyword('');
    setClientOptions([]);
    setClientDropdownOpen(false);
    setMobileDropdownOpen(false);
  };

  const handleRoleChange = (role) => {
    setFormData({ ...createEmptyUser(), Role: role });
    setClientKeyword('');
    setMobileKeyword('');
    setClientOptions([]);
  };

  const handleClientSelect = (client) => {
    setFormData((prev) => ({
      ...prev,
      ClientId: client.Id,
      EmployeeId: '',
      Client: {
        Id: client.Id,
        Name: client.Name || '',
        MobileNo1: client.MobileNo1 || '',
        EmailId: client.EmailId || '',
      },
      Employee: {},
    }));
    setClientKeyword(client.Name || '');
    setMobileKeyword(client.MobileNo1 || '');
    setClientDropdownOpen(false);
    setMobileDropdownOpen(false);
  };

  const handleEmployeeSelect = (employeeId) => {
    const id = Number(employeeId) || '';
    const employee = employees.find((item) => item.Id === id) || {};
    setFormData((prev) => ({
      ...prev,
      EmployeeId: id,
      ClientId: '',
      Employee: employee,
      Client: {},
    }));
  };

  const handleEdit = async (user) => {
    const next = {
      ...createEmptyUser(),
      ...user,
      Password: '',
      CPassword: '',
      ClientId: user.ClientId || '',
      EmployeeId: user.EmployeeId || '',
      Client: user.Client || {},
      Employee: user.Employee || {},
      IsSkip: false,
    };

    setFormData(next);
    setClientKeyword(next.Client?.Name || '');
    setMobileKeyword(next.Client?.MobileNo1 || '');
    await loadEmployees(next.EmployeeId || 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateUser = (skipPassword = false) => {
    if (!formData.UserName.trim()) {
      setError('Enter User Name');
      return false;
    }

    if (isClientRole && !formData.ClientId) {
      setError('Select client');
      return false;
    }

    if (!isClientRole && !formData.EmployeeId) {
      setError('Select employee');
      return false;
    }

    if (!skipPassword) {
      if (!formData.Password) {
        setError('Enter Password');
        return false;
      }

      if (!formData.CPassword) {
        setError('Enter Confirm Password');
        return false;
      }

      if (formData.Password !== formData.CPassword) {
        setError('Password and confirm password do not match');
        return false;
      }
    }

    return true;
  };

  const saveUser = async (skipPassword = false) => {
    if (!validateUser(skipPassword)) return;

    try {
      setSaving(true);
      setError('');
      const payload = {
        ...formData,
        UserName: formData.UserName.trim(),
        ClientId: isClientRole ? formData.ClientId : null,
        EmployeeId: isClientRole ? null : formData.EmployeeId,
        Client: isClientRole ? formData.Client : {},
        Employee: isClientRole ? {} : formData.Employee,
        IsSkip: skipPassword,
      };

      const response = await MainService.SaveUserV2(payload);
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess(isEditing ? 'User updated successfully' : 'User created successfully');
        resetForm();
        await loadEmployees(0);
        await loadUsers();
      } else {
        setError(getMessage(response, 'Failed to save user'));
      }
    } catch (err) {
      console.error('Error saving user:', err);
      setError('Failed to save user. Please check whether the V2 user API is deployed.');
    } finally {
      setSaving(false);
    }
  };

  const confirmBlock = async () => {
    if (!userToBlock?.Id) return;

    try {
      setLoading(true);
      const response = await MainService.BlockUserV2(userToBlock.Id);
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess(userToBlock.IsBlocked ? 'User unblocked successfully' : 'User blocked successfully');
        setUserToBlock(null);
        await loadUsers();
      } else {
        setError(getMessage(response, 'Failed to update user status'));
      }
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm.trim());
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveSearchKeyword('');
    setRoleFilter('All');
    setStatusFilter('All');
    setCurrentPage(1);
  };

  const refreshAll = async () => {
    resetForm();
    setSearchTerm('');
    setActiveSearchKeyword('');
    setCurrentPage(1);
    await loadEmployees(0);
    await loadUsers();
  };

  const personName = (user) => (user.Role === 'Client' ? user.Client?.Name : user.Employee?.Name) || '-';
  const personMobile = (user) => (user.Role === 'Client' ? user.Client?.MobileNo1 : user.Employee?.MobileNo1) || '-';
  const personEmail = (user) => (user.Role === 'Client' ? user.Client?.EmailId : user.Employee?.EmailId) || '';

  const renderClientDropdown = (open) => {
    if (!open) return null;

    return (
      <div className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
        {clientLoading ? (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading clients...
          </div>
        ) : clientOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">Type at least 2 letters or numbers to search available clients.</div>
        ) : (
          clientOptions.map((client) => (
            <button
              key={client.Id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleClientSelect(client)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700">
                {initials(client.Name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">{client.Name || '-'}</div>
                <div className="truncate text-xs text-gray-500">
                  {client.MobileNo1 || '-'} {client.ClientNo ? `| ${client.ClientNo}` : ''}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    );
  };

  const statCards = [
    { label: 'Total Users', value: summary.Total, icon: UsersIcon, className: 'border-gray-300 bg-gray-200 text-gray-800' },
    { label: 'Active', value: summary.Active, icon: ShieldCheck, className: 'border-green-200 bg-green-50 text-green-700' },
    { label: 'Blocked', value: summary.Blocked, icon: Lock, className: 'border-red-200 bg-red-50 text-red-700' },
    { label: 'Admin Users', value: summary.Admin, icon: ShieldCheck, className: 'border-purple-200 bg-purple-50 text-purple-700' },
    { label: 'Employee Users', value: summary.Employee, icon: UserCheck, className: 'border-amber-200 bg-amber-50 text-amber-700' },
    { label: 'Driver Users', value: summary.Driver, icon: UserCog, className: 'border-blue-200 bg-blue-50 text-blue-700' },
    { label: 'Client Users', value: summary.Client, icon: Building2, className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed right-4 top-20 z-40 flex min-w-[320px] items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed right-4 top-20 z-40 flex min-w-[320px] items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <UserCog className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Users</h1>
              <p className="mt-1 text-sm text-gray-500">Create portal users and manage access.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={refreshAll}
              disabled={loading || saving}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`rounded-xl border p-5 shadow-sm ${card.className}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-75">{card.label}</p>
                  <p className="mt-2 text-3xl font-bold">{card.value || 0}</p>
                </div>
                <div className="rounded-xl bg-white/70 p-3">
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <UserCog className="h-5 w-5 text-primary" />
              {isEditing ? 'Update User' : 'Create User'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isEditing ? 'Update login details or use Skip Password to keep the old password.' : 'Choose a role and link the user to a client or employee.'}
            </p>
          </div>

          <div className="space-y-5 p-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Role</label>
              <div className="grid grid-cols-2 gap-2">
                {FORM_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleRoleChange(role)}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold ring-1 transition ${
                      formData.Role === role
                        ? roleStyle(role)
                        : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            {isClientRole ? (
              <div className="grid grid-cols-1 gap-4">
                <div className="relative">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Client Name</label>
                  <Search className="absolute left-3 top-[38px] h-4 w-4 text-gray-400" />
                  <input
                    value={clientKeyword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setClientKeyword(value);
                      setFormData((prev) => ({ ...prev, ClientId: '', Client: { ...prev.Client, Name: value } }));
                      setClientDropdownOpen(true);
                    }}
                    onFocus={() => setClientDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setClientDropdownOpen(false), 150)}
                    className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 focus:border-transparent focus:ring-2 focus:ring-primary"
                    placeholder="Search available clients"
                  />
                  {renderClientDropdown(clientDropdownOpen)}
                </div>

                <div className="relative">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Client Mobile No</label>
                  <Phone className="absolute left-3 top-[38px] h-4 w-4 text-gray-400" />
                  <input
                    value={mobileKeyword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMobileKeyword(value);
                      setFormData((prev) => ({ ...prev, ClientId: '', Client: { ...prev.Client, MobileNo1: value } }));
                      setMobileDropdownOpen(true);
                    }}
                    onFocus={() => setMobileDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setMobileDropdownOpen(false), 150)}
                    className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 focus:border-transparent focus:ring-2 focus:ring-primary"
                    placeholder="Search by mobile"
                  />
                  {renderClientDropdown(mobileDropdownOpen)}
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Employee</label>
                <select
                  value={formData.EmployeeId || ''}
                  onChange={(e) => handleEmployeeSelect(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-transparent focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee.Id} value={employee.Id}>
                      {employee.Name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-sm font-bold text-primary ring-1 ring-gray-200">
                  {initials(selectedAccountName)}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{selectedAccountName || 'No account selected'}</p>
                  <p className="truncate text-xs text-gray-500">{selectedAccountMobile || 'Select a client or employee for this login'}</p>
                </div>
              </div>
              {selectedAccountEmail ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                  <Mail className="h-3.5 w-3.5" />
                  {selectedAccountEmail}
                </div>
              ) : null}
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">User Name</label>
                <input
                  value={formData.UserName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, UserName: e.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-transparent focus:ring-2 focus:ring-primary"
                  placeholder="Enter login username"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={formData.Password}
                    onChange={(e) => setFormData((prev) => ({ ...prev, Password: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-transparent focus:ring-2 focus:ring-primary"
                    placeholder={isEditing ? 'New password' : 'Password'}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Confirm</label>
                  <input
                    type="password"
                    value={formData.CPassword}
                    onChange={(e) => setFormData((prev) => ({ ...prev, CPassword: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 focus:border-transparent focus:ring-2 focus:ring-primary"
                    placeholder="Confirm password"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" />
                Clear
              </button>
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => saveUser(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <SkipForward className="h-4 w-4" />
                  Skip Pass.
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => saveUser(false)}
                disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : isEditing ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <UsersIcon className="h-5 w-5 text-primary" />
                  User Directory
                </h2>
                <p className="mt-1 text-sm text-gray-500">Showing 20 users per page</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyFilters();
                    }}
                    className="w-64 rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
                    placeholder="Search user/client/employee"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyFilters}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark"
                >
                  <Search className="h-4 w-4" />
                  Search
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-transparent text-sm font-medium text-gray-700 outline-none"
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role === 'All' ? 'All Roles' : role}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                <ShieldCheck className="h-4 w-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-transparent text-sm font-medium text-gray-700 outline-none"
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status === 'All' ? 'All Status' : status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="max-h-[540px] overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">User</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Linked Account</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Options</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-gray-500">
                      <RefreshCw className="mx-auto mb-3 h-8 w-8 animate-spin text-gray-400" />
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                        <UsersIcon className="h-7 w-7 text-gray-400" />
                      </div>
                      <p className="font-semibold text-gray-800">No users found</p>
                      <p className="text-sm text-gray-500">Try changing filters or create a new user.</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.Id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-700">
                            {initials(user.UserName)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{user.UserName || '-'}</p>
                            <p className="text-xs text-gray-500">ID: {user.Id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${roleStyle(user.Role)}`}>
                          {user.Role || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">{personName(user)}</p>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {personMobile(user)}
                          </span>
                          {personEmail(user) ? (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {personEmail(user)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            user.IsBlocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {user.IsBlocked ? <Lock className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                          {user.IsBlocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(user)}
                            className="rounded-lg p-2 text-yellow-600 transition-colors hover:bg-yellow-50 hover:text-yellow-800"
                            title="Edit user"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setUserToBlock(user)}
                            className={`rounded-lg p-2 transition-colors ${
                              user.IsBlocked ? 'text-green-600 hover:bg-green-50 hover:text-green-800' : 'text-red-600 hover:bg-red-50 hover:text-red-800'
                            }`}
                            title={user.IsBlocked ? 'Unblock user' : 'Block user'}
                          >
                            {user.IsBlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-sm text-gray-600">
              Showing {users.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1 || loading}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-2 text-sm font-medium text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages || loading}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {userToBlock &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
                <div className={`rounded-full p-2 ${userToBlock.IsBlocked ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                  {userToBlock.IsBlocked ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{userToBlock.IsBlocked ? 'Unblock User' : 'Block User'}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Are you sure you want to {userToBlock.IsBlocked ? 'unblock' : 'block'} "{userToBlock.UserName}"?
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setUserToBlock(null)}
                  disabled={loading}
                  className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5">
                <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                  <p><span className="font-semibold">User:</span> {userToBlock.UserName}</p>
                  <p><span className="font-semibold">Role:</span> {userToBlock.Role}</p>
                  <p><span className="font-semibold">Linked:</span> {personName(userToBlock)}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setUserToBlock(null)}
                  disabled={loading}
                  className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmBlock}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                    userToBlock.IsBlocked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {loading ? 'Saving...' : userToBlock.IsBlocked ? 'Confirm Unblock' : 'Confirm Block'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Users;
